/**
 * Нормализация телефонов Беларуси и России.
 *
 * Зачем это отдельный модуль: телефон — ключ дедупликации кандидатов (Р-09).
 * В исходной Google-таблице один и тот же номер записан десятком способов:
 *   375(29)136-48-45 · +375 29 136 48 45 · 80291364845 · 291364845
 *   7(993)348-99-34  · 8 993 348 99 34   · +79933489934
 * Пока номер не приведён к канонической форме, «этот кандидат уже есть»
 * не определяется, и база превращается в свалку дублей.
 *
 * Канонический вид — только цифры, с кодом страны: 375291364845 / 79933489934.
 * Сырое значение, как его ввёл пользователь, сохраняется отдельным полем
 * и показывается в гриде — рекрутёры узнают номера в привычном виде.
 */

export type PhoneCountry = 'BY' | 'RU';

/** Коды мобильных операторов Беларуси. */
export const BY_MOBILE_CODES = ['25', '29', '33', '44'] as const;

export interface NormalizedPhone {
  /** Только цифры, с кодом страны: 375291364845 */
  e164: string;
  /** Форматированный для показа: +375 (29) 136-48-45 */
  formatted: string;
  country: PhoneCountry;
}

/** Убирает всё, кроме цифр. */
function digitsOnly(input: string): string {
  return input.replace(/\D+/g, '');
}

/**
 * Приводит телефон к каноническому виду.
 *
 * @param input сырая строка от пользователя или из импорта
 * @param hint  ожидаемая страна: для колонки «Телефон РБ» — 'BY',
 *              для «Телефон РФ» — 'RU'. Разрешает неоднозначность
 *              коротких записей вида «8 029 …» против «8 999 …».
 * @returns null, если распознать не удалось — вызывающий код решает,
 *          отвергнуть значение или сохранить как есть.
 */
export function normalizePhone(input: string | null | undefined, hint?: PhoneCountry): NormalizedPhone | null {
  if (!input) return null;

  // Прочерки, которыми в таблице отмечают «номера нет»
  const trimmed = input.trim();
  if (!trimmed || /^[-–—\s.]+$/.test(trimmed)) return null;

  let d = digitsOnly(trimmed);
  if (d.length < 9) return null;

  // ── Беларусь ───────────────────────────────────────────────
  // 375 XX XXXXXXX (12 цифр)
  if (d.startsWith('375') && d.length === 12) {
    return buildBy(d.slice(3));
  }
  // 8 0XX XXXXXXX — белорусский междугородний формат (11 цифр)
  if (d.startsWith('80') && d.length === 11) {
    return buildBy(d.slice(2));
  }

  // ── Россия ─────────────────────────────────────────────────
  // 7 9XX XXXXXXX или 8 9XX XXXXXXX (11 цифр)
  if ((d.startsWith('7') || d.startsWith('8')) && d.length === 11) {
    const national = d.slice(1);
    // «8 029 …» — это Беларусь, а не Россия: у РФ национальный номер
    // начинается с 9 (мобильные) или с кода региона 3/4/8.
    if (national.startsWith('0')) {
      return buildBy(national.slice(1));
    }
    return buildRu(national);
  }

  // ── Короткие записи без кода страны ────────────────────────
  // 9 цифр: белорусский национальный номер (29 1364845)
  if (d.length === 9) {
    const code = d.slice(0, 2);
    if ((BY_MOBILE_CODES as readonly string[]).includes(code)) return buildBy(d);
    if (hint === 'BY') return buildBy(d);
    return null;
  }

  // 10 цифр: российский национальный номер (993 3489934)
  if (d.length === 10) {
    if (d.startsWith('9') || hint === 'RU') return buildRu(d);
    return null;
  }

  // Ведущий мусор вида «00375…»
  if (d.startsWith('00')) {
    d = d.slice(2);
    return normalizePhone(d, hint);
  }

  return null;
}

function buildBy(national: string): NormalizedPhone | null {
  if (national.length !== 9) return null;
  const code = national.slice(0, 2);
  const rest = national.slice(2);
  return {
    e164: `375${national}`,
    formatted: `+375 (${code}) ${rest.slice(0, 3)}-${rest.slice(3, 5)}-${rest.slice(5, 7)}`,
    country: 'BY',
  };
}

function buildRu(national: string): NormalizedPhone | null {
  if (national.length !== 10) return null;
  const code = national.slice(0, 3);
  const rest = national.slice(3);
  return {
    e164: `7${national}`,
    formatted: `+7 (${code}) ${rest.slice(0, 3)}-${rest.slice(3, 5)}-${rest.slice(5, 7)}`,
    country: 'RU',
  };
}

/** Проверка без получения результата — для Zod-валидаторов. */
export function isValidPhone(input: string, hint?: PhoneCountry): boolean {
  const n = normalizePhone(input, hint);
  if (!n) return false;
  if (hint && n.country !== hint) return false;
  return true;
}

/**
 * Формат для показа. Если номер не распознан — возвращает исходную строку:
 * данные пользователя не теряем никогда, даже кривые.
 */
export function formatPhone(input: string | null | undefined, hint?: PhoneCountry): string {
  if (!input) return '';
  return normalizePhone(input, hint)?.formatted ?? input;
}

// ─────────────────────────────────────────────────────────────
// Несколько номеров в одном поле
// ─────────────────────────────────────────────────────────────

/**
 * Разделители между номерами.
 *
 * Только явные — запятая, точка с запятой, косая черта, перевод строки.
 * Пробел разделителем не считается намеренно: «+375 29 136 48 45» записан
 * через пробелы, и по ним строка развалилась бы на четыре обломка.
 */
const PHONE_SEPARATORS = /[,;/\n|]+/;

export interface PhoneListItem {
  /** Как ввёл пользователь, без окружающих пробелов */
  raw: string;
  /** null, если распознать не удалось */
  normalized: NormalizedPhone | null;
}

/**
 * Разбор поля с несколькими номерами.
 *
 * У одного человека номеров бывает много: свой белорусский, российский,
 * рабочий, номер жены, номер, по которому «звонить после шести». В исходной
 * таблице их писали в одну ячейку через запятую, и при переносе второй номер
 * приходилось отправлять в комментарий — то есть терять его для поиска.
 *
 * Порядок сохраняется: первый номер считается основным (он попадает
 * в колонку канонического вида, по которой идёт сортировка).
 */
export function parsePhoneList(
  input: string | null | undefined,
  hint?: PhoneCountry,
): PhoneListItem[] {
  if (!input) return [];

  return input
    .split(PHONE_SEPARATORS)
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && !/^[-–—.\s]+$/.test(part))
    .map((raw) => ({ raw, normalized: normalizePhone(raw, hint) }));
}

/** Канонические номера строки, без нераспознанных и без повторов. */
export function phoneListE164(input: string | null | undefined, hint?: PhoneCountry): string[] {
  const seen = new Set<string>();
  for (const item of parsePhoneList(input, hint)) {
    if (item.normalized) seen.add(item.normalized.e164);
  }
  return [...seen];
}

/**
 * Проверка поля целиком: строка годится, если распознан каждый номер в ней.
 *
 * Пустая строка допустима — телефона может не быть вовсе; это решает
 * `nullish()` в схеме, а не эта проверка.
 */
export function isValidPhoneList(input: string, hint?: PhoneCountry): boolean {
  const items = parsePhoneList(input, hint);
  if (items.length === 0) return false;
  return items.every((item) => item.normalized !== null && (!hint || item.normalized.country === hint));
}

/**
 * Тот же номер, записанный дважды, оставляем один раз.
 *
 * Номера в поле копят вставкой: пришёл ещё один — дописали в конец. При
 * этом легко вставить тот, который уже есть, особенно если записан он
 * иначе: «+375291234567» и «375 (29) 123-45-67» — один и тот же человек
 * на одном и том же конце провода. В ячейке они выглядели двумя разными
 * строками, и рекрутёр звонил по второй, выяснив это только гудком.
 *
 * Сравниваем по каноническому виду, а показываем — как записал человек:
 * из повторов остаётся первый. Нераспознанное («звонить после шести»,
 * добавочный) не трогаем вовсе: своего канонического вида у него нет,
 * а выбрасывать чужую пометку мы не вправе.
 */
export function dedupePhoneList(input: string | null | undefined, hint?: PhoneCountry): string {
  const seen = new Set<string>();
  const kept: string[] = [];

  for (const item of parsePhoneList(input, hint)) {
    const key = item.normalized?.e164;
    if (key) {
      if (seen.has(key)) continue;
      seen.add(key);
    }
    kept.push(item.raw);
  }
  return kept.join('\n');
}

/**
 * Номер для буфера обмена: `+375291364845`.
 *
 * Показываем мы номер по-человечески — «+375 (29) 136-48-45», — но копируют
 * его не для чтения. Копируют, чтобы вставить: в поиск Telegram, в WhatsApp,
 * в звонилку. Ни один из них не разбирает скобки, пробелы и дефисы — Telegram
 * по такому тексту не находит никого и не делает из него ссылку, хотя номер
 * в буфере правильный. Рекрутёр в этот момент чистит номер вручную.
 *
 * Плюс обязателен: без него «375291364845» читается как число, а не как
 * международный номер.
 *
 * Нераспознанное отдаём как есть. В поле попадают и пометки вроде «звонить
 * после шести», и кривые записи — своего канонического вида у них нет,
 * а терять их при копировании мы не вправе.
 *
 * Несколько номеров разделяем запятой с пробелом: между номерами разделитель
 * нужен, внутри номера — нет.
 */
export function phoneForCopy(input: string | null | undefined, hint?: PhoneCountry): string {
  const items = parsePhoneList(input, hint);
  if (items.length === 0) return (input ?? '').trim();
  return items.map((item) => (item.normalized ? `+${item.normalized.e164}` : item.raw)).join(', ');
}

/** Первый распознанный номер — он же основной. */
export function primaryPhone(
  input: string | null | undefined,
  hint?: PhoneCountry,
): NormalizedPhone | null {
  for (const item of parsePhoneList(input, hint)) {
    if (item.normalized) return item.normalized;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Маска ввода
// ─────────────────────────────────────────────────────────────

/** Сколько цифр в национальном номере, без кода страны. */
const NATIONAL_LENGTH: Record<PhoneCountry, number> = { BY: 9, RU: 10 };

/**
 * Национальные цифры из того, что человек успел набрать.
 *
 * Отличие от `normalizePhone` — в терпимости к незаконченному: там номер либо
 * распознан целиком, либо нет, а здесь строка обязана оставаться читаемой
 * после каждой нажатой цифры.
 *
 * Код страны срезаем только там, где он однозначен. «8452…» — это Саратов,
 * а не восьмёрка перед номером: национальный мобильный в РФ начинается
 * с девятки, и по ней восьмёрка отличается от кода города.
 */
function nationalDigits(digits: string, country: PhoneCountry): string {
  const max = NATIONAL_LENGTH[country];

  /*
    Ведущую цифру набора снимаем сразу, а не когда номер дописан.

    Дождаться «лишней» цифры и только тогда решить, что это код страны,
    нельзя: маска обрезает набранное по длине номера, и до правила эта
    цифра просто не доживает — «89261234567» превращалось в «+7 (892)
    612-34-56».

    Восьмёрку съедаем ровно один раз. Городской код в России тоже
    начинается с восьмёрки — 845 Саратов, 812 Петербург, — но набирают
    такой номер так же, как звонят: «8 845 2…». Первая восьмёрка уходит
    как выход на межгород, вторая остаётся кодом города.
  */
  let d = digits;

  if (country === 'BY') {
    // «375» в начале — это код страны: белорусского кода оператора,
    // начинающегося на 37, не существует
    if (d.startsWith('375')) d = d.slice(3);
    else {
      if (d.startsWith('8')) d = d.slice(1);
      // «8 0XX» — междугородний набор; с нуля не начинается ни один код
      if (d.startsWith('0')) d = d.slice(1);
    }
  } else if (d.startsWith('7') || d.startsWith('8')) {
    // С семёрки не начинается ни один российский код: 3xx, 4xx, 8xx, 9xx
    d = d.slice(1);
  }

  return d.slice(0, max);
}

/**
 * Цифры номера без кода страны — то, что маска на самом деле держит.
 *
 * Считать их отдельно приходится потому, что маска сама дописывает «+7»,
 * и при повторном разборе своего же вывода этот код страны засчитывался
 * за часть номера: «123321321» — девять цифр мусора — превращалось
 * в «+7 (123) 321-32-1», а оттуда читалось как десятизначный номер
 * и проходило проверку. Поэтому свой префикс мы срезаем как текст,
 * а общие правила («375…», «80…», «8 9…») применяем только к тому,
 * что человек написал сам.
 */
export function phoneInputDigits(input: string, country: PhoneCountry): string {
  const prefix = country === 'BY' ? '+375' : '+7';
  const s = (input ?? '').trim();

  if (s.startsWith(prefix)) {
    return digitsOnly(s.slice(prefix.length)).slice(0, NATIONAL_LENGTH[country]);
  }
  return nationalDigits(digitsOnly(s), country);
}

/** Номер набран целиком — по нему уже можно искать дубли. */
export function isCompletePhoneInput(input: string, country: PhoneCountry): boolean {
  return phoneInputDigits(input, country).length === NATIONAL_LENGTH[country];
}

/**
 * Как выглядит номер, пока его набирают.
 *
 * Возвращает то, что должно стоять в поле: «+375 (29) 136-48-45» целиком
 * и «+375 (29) 136-4» на полпути. Всё, что не цифра, отбрасывается — ввести
 * в поле телефона «звонить после шести» больше нельзя, а лишние цифры сверх
 * длины номера просто не набираются.
 *
 * Пустой ввод даёт пустую строку, а не «+375 (»: подсказку про код страны
 * показывает placeholder, и дорисовывать её в пустое поле значит мешать
 * человеку стереть номер.
 */
export function formatPhoneInput(input: string, country: PhoneCountry): string {
  return formatPhoneDigits(phoneInputDigits(input, country), country);
}

/**
 * То же самое, но из готовых цифр номера — без снятия кода страны.
 *
 * Нужна там, где цифры уже отобраны и трогать их нельзя: стирание по одной
 * цифре. Пропустить их ещё раз через общие правила значит потерять номер
 * вроде саратовского 845, у которого код города начинается с восьмёрки.
 */
export function formatPhoneDigits(national: string, country: PhoneCountry): string {
  if (national.length === 0) return '';

  const prefix = country === 'BY' ? '+375' : '+7';
  const codeLength = country === 'BY' ? 2 : 3;

  const code = national.slice(0, codeLength);
  const rest = national.slice(codeLength);

  let out = `${prefix} (${code}`;
  // Скобка закрывается вместе с кодом оператора: пока он не набран,
  // закрывать нечего
  if (national.length < codeLength) return out;

  out += ') ';
  out += rest.slice(0, 3);
  if (rest.length > 3) out += `-${rest.slice(3, 5)}`;
  if (rest.length > 5) out += `-${rest.slice(5, 7)}`;
  return out;
}
